// Share one download; hidden PDP sliders do not need Swiper during first paint.
let Swiper, swiperImport;
const loadSwiper = () => swiperImport ||= import("./swiper-bundle.esm.browser.min.js")
  .then(module => (Swiper = module.default))
  .catch(error => { swiperImport = null; throw error; });

if (!customElements.get("slideshow-section")) {
  customElements.define(
    "slideshow-section",
    class Slider extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.configuration = null;
        this.handleKeyboard = this.handleKeyboard.bind(this);
        this.start = this.start.bind(this);
        this.matchResolution = this.matchResolution.bind(this);
        this.centerNavigation = this.centerNavigation.bind(this);
        this.initializeOrDestroySwiperForBrands = this.initializeOrDestroySwiperForBrands.bind(this);
      }

      handleTabindex(swiper) {
        if (!swiper) return;
        const grouped = this.hasAttribute("data-slides-group");
        const selectors = "a, button, input, textarea, select, [tabindex]";
        const overflow = swiper.slides.length > swiper.params.slidesPerView;
        // Read geometry once, before changing any tabindex attributes.
        const bounds = grouped && overflow ? this.getBoundingClientRect() : null;
        const visible = swiper.slides.map((slide, index) => {
          if (!overflow) return true;
          if (!grouped) return index === swiper.activeIndex;
          const rect = slide.getBoundingClientRect();
          return rect.left >= bounds.left && rect.right <= bounds.right &&
            rect.top >= bounds.top && rect.bottom <= bounds.bottom;
        });
        swiper.slides.forEach((slide, index) => {
          slide.querySelectorAll(selectors).forEach(el => {
            const value = visible[index] && !el.hasAttribute("data-omit-tabindex") ? "0" : "-1";
            if (el.getAttribute("tabindex") !== value) el.setAttribute("tabindex", value);
          });
        });
      }

      handleKeyboard(event) {
        const keyCode = event.keyCode || event.which;
        const focusedElement = document.activeElement;

        if (this.swiper && this.swiper.el.contains(focusedElement)) {
          switch (keyCode) {
            case 37: // Left arrow
              this.swiper.slidePrev();
              break;
            case 39: // Right arrow
              this.swiper.slideNext();
              break;
          }
        }
      }

      connectedCallback() {
        // Empty recommendation shells are replaced by their own component after fetch.
        if (!this.querySelector("[data-swiper-slide]")) return;
        if ((document.querySelector("[data-lv29], #product-grid") || this.closest("cart-drawer")) && !window.Shopify?.designMode && "IntersectionObserver" in window) {
          // Preserve the normal horizontal layout while waiting for visibility.
          if (!(this.dataset.brands === "true" && window.innerWidth >= 900)) {
          this.querySelector("[data-swiper]")?.classList.add("swiper", "wt-slider__container");
          this.querySelector("[data-swiper-container]")?.classList.add("swiper-wrapper", "wt-slider__wrapper");
          this.querySelectorAll("[data-swiper-slide]").forEach(slide => slide.classList.add("swiper-slide", "wt-slider__slide"));
          }
          this.addEventListener("pointerenter", this.start, {passive: true});
          this.addEventListener("focusin", this.start);
          this.visibilityObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio > 0)) this.start();
          }, {rootMargin: "250px 0px"});
          this.visibilityObserver.observe(this);
        } else if (document.body.classList.contains("template-index") && this.closest(".wt-featured-collection, video-reels") && !window.Shopify?.designMode && "IntersectionObserver" in window && this.getBoundingClientRect().top > window.innerHeight + 100) {
          // Match Swiper's responsive geometry before deferring its heavier setup.
          this.readConfiguration();
          this.previewLayout = this.previewLayout.bind(this);
          this.previewLayout();
          window.addEventListener("resize", this.previewLayout);
          this.addEventListener("pointerenter", this.start, {passive: true});
          this.addEventListener("focusin", this.start);
          this.visibilityObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) this.start();
          }, {rootMargin: "500px 0px"});
          this.visibilityObserver.observe(this);
        } else if (document.body.classList.contains("template-index") && !window.Shopify?.designMode && "requestIdleCallback" in window && this.getBoundingClientRect().top > window.innerHeight + 100) {
          this.addEventListener("pointerenter", this.start, {passive: true});
          this.addEventListener("focusin", this.start);
          this.idleStart = requestIdleCallback(() => this.start(), {timeout: 1200});
        } else this.start();
      }

      previewLayout() {
        const base = this.configuration;
        const enabled = !base.enableOnMedia || matchMedia(base.enableOnMedia).matches;
        if (!enabled) {
          this.removeAttribute("data-lv89-waiting");
          return;
        }
        const breakpoint = Object.keys(base.breakpoints || {})
          .filter(value => Number(value) <= window.innerWidth)
          .sort((a, b) => Number(a) - Number(b)).pop();
        const options = {...base, ...(base.breakpoints?.[breakpoint] || {})};
        const count = Number(options.slidesPerView);
        if (!Number.isFinite(count) || count <= 0) {
          // Auto-width layouts keep the established initialization path.
          this.start();
          return;
        }
        const gap = Number(options.spaceBetween) || 0;
        this.setAttribute("data-lv89-waiting", "");
        this.querySelector("[data-swiper]")?.classList.remove("loading");
        this.querySelector("[data-swiper-container]")?.classList.remove("loading");
        this.style.setProperty("--lv89-slide-width", `calc((100% - ${(count - 1) * gap}px) / ${count})`);
        this.style.setProperty("--lv89-slide-gap", `${gap}px`);
        this.querySelector("[data-swiper]")?.classList.add("swiper", "wt-slider__container");
        this.querySelector("[data-swiper-container]")?.classList.add("swiper-wrapper", "wt-slider__wrapper");
        this.querySelectorAll("[data-swiper-slide]").forEach(slide => slide.classList.add("swiper-slide", "wt-slider__slide"));
      }

      async start() {
        if (this.started || this.starting) return;
        if (this.idleStart) cancelIdleCallback(this.idleStart);
        this.idleStart = 0;
        this.starting = true;
        try { await loadSwiper(); }
        catch (error) { this.starting = false; return; }
        this.starting = false;
        if (!this.isConnected) return;
        this.started = true;
        this.removeAttribute("data-lv89-waiting");
        window.removeEventListener("resize", this.previewLayout);
        this.visibilityObserver?.disconnect();
        this.removeEventListener("pointerenter", this.start);
        this.removeEventListener("focusin", this.start);
        this.readConfiguration();
        this.shouldSkipCenterNavMethod =
          this.dataset.skipCenterNavMethod === "true";
        // Initialize the swiper based on conditions
        if (window.innerWidth < 900 && !this.swiper) {
          this.swiperInitilize();
        }

        // Register resize event listeners
        window.addEventListener("resize", this.centerNavigation);

        if (this.dataset.brands === "true") {
          window.addEventListener(
            "resize",
            this.initializeOrDestroySwiperForBrands,
          );
        } else if (this.configuration.enableOnMedia) {
          window.addEventListener("resize", this.matchResolution);
          this.breakpoint = window.matchMedia(this.configuration.enableOnMedia);
          this.matchResolution();
        } else if (!this.swiper) {
          this.swiperInitilize();
        }

        // Add keyboard event listener
        window.addEventListener("keydown", this.handleKeyboard);
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.previewLayout);
        this.removeAttribute("data-lv89-waiting");
        if (this.idleStart) cancelIdleCallback(this.idleStart);
        this.idleStart = 0;
        this.visibilityObserver?.disconnect();
        this.removeEventListener("pointerenter", this.start);
        this.removeEventListener("focusin", this.start);
        window.removeEventListener("resize", this.matchResolution);
        this.started = false;
        if (this.swiper) this.swiperDestroy();
        window.removeEventListener(
          "resize",
          this.initializeOrDestroySwiperForBrands,
        );
        window.removeEventListener("resize", this.centerNavigation);
        window.removeEventListener("keydown", this.handleKeyboard);
      }

      initializeOrDestroySwiperForBrands() {
        if (window.innerWidth < 900) {
          if (!this.swiper) this.swiperInitilize();
        } else if (this.swiper) this.swiperDestroy();
      }

      centerNavigation() {
        if (window.innerWidth < 900 || this.shouldSkipCenterNavMethod) return;

        const picture = this.querySelector("picture")?.classList.contains(
          "hero__pic--mobile",
        )
          ? this.querySelectorAll("picture")[1]
          : this.querySelector("picture");

        if (picture) {
          const boundingClientRectPic = picture.getBoundingClientRect();

          const btns = this.querySelectorAll(".wt-slider__nav-btn");
          btns.forEach(
            (btn) =>
              (btn.style.top = `${22 + boundingClientRectPic.height / 2}px`),
          );
        }
      }

      readConfiguration() {
        const default_configuration = {
          autoHeight: false,
          slidesPerView: 1,
          autoplay: false,
          threshold: 5,
          pagination: {
            el: ".swiper-pagination",
            renderBullet(index, className) {
              return `<span class="${className} swiper-pagination-bullet--svg-animation"><svg width="20" height="20" viewBox="0 0 28 28"><circle class="svg__circle" cx="14" cy="14" r="12" fill="none" stroke-width="2"></circle><circle class="svg__circle-inner" cx="14" cy="14" r="5" stroke-width="2"></circle></svg></span>`;
            },
          },
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: false,
          on: {
            afterInit: (swiper) => {
              const dataSwiper = this.querySelector("[data-swiper]");
              const dataSwiperContainer = this.querySelector(
                "[data-swiper-container]",
              );
              dataSwiper?.classList.remove("loading");
              dataSwiperContainer?.classList.remove("loading");

              this.centerNavigation();
              this.handleTabindex(swiper);
            },
            slideChangeTransitionEnd: (swiper) => {
              this.handleTabindex(swiper);
            },
          },
        };

        const get_custom_configuration = this.querySelector(
          "[data-swiper-configuration]",
        )?.innerHTML;
        const custom_configuration = get_custom_configuration
          ? JSON.parse(get_custom_configuration)
          : {};
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
        };

        if (this.configuration.autoplay) {
          if (window.innerWidth < 900) {
            var override_configuration = {
              autoplay: false,
            };
          }
          this.configuration = {
            ...this.configuration,
            ...override_configuration,
          };
        }
      }

      matchResolution() {
        if (this.breakpoint.matches === true) {
          if (!this.swiper) {
            this.swiperInitilize();
          }
        } else if (this.swiper) {
          this.swiperDestroy();
        }
      }

      swiperInitilize() {
        const node = this;
        const container = this.querySelector("[data-swiper]");
        this.querySelector("[data-swiper]").classList.add(
          "swiper",
          "wt-slider__container",
        );
        this.querySelector("[data-swiper-container]").classList.add(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        this.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add("swiper-slide", "wt-slider__slide");
        });
        this.swiper = new Swiper(container, this.configuration);
        this.dispatchEvent(new CustomEvent("lv87:slider-ready", {bubbles: true}));
      }

      swiperDestroy() {
        this.querySelector("[data-swiper]").classList.remove(
          "swiper",
          "wt-slider__container",
        );
        this.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        this.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
        this.swiper.destroy();
        if (this.querySelector(".swiper-pagination"))
          this.querySelector(".swiper-pagination").innerHTML = "";
        this.swiper = null;
      }

      slideTo(slide) {
        this.swiper.autoplay.stop();
        const index = Array.from(slide.parentNode.children).indexOf(slide);
        this.swiper.slideTo(index);
      }
    },
  );
}
